import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { debounce } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { EmailActivityWrapperObject } from '@databox/ember-integrations/lib/components/EmailActivity/EmailActivity';
import EmailActivity, {
	type EmailActivityEntryPointProps,
} from '@databox/ember-integrations/lib/components/EmailActivity/EmailActivityEntryPoint';
import moment from 'moment';

import type { APIData, ReactIntegrations, Service } from 'webapp-types';
import type { ActivityLogEvent } from 'webapp/enums/activity-log';
import {
	ActivityLogFeature,
	ActivityLogFetchType,
	ActivityLogObjectType,
} from 'webapp/enums/activity-log';
import { AppEvent } from 'webapp/enums/events';
import { equal } from 'webapp/utils/common';

import didInsert from 'webapp/modifiers/did-insert';
import { containerEventName } from 'webapp/modifiers/visible-check-container';
import willDestroy from 'webapp/modifiers/will-destroy';
import AccountLogsPageNavigationComponent from 'webapp/components/account/logs/page-navigation';
import AccountLogsTableComponent from 'webapp/components/account/logs/table';
import FilterLineProxyComponent from 'webapp/components/filter/line-proxy';
import FilterNoResultsComponent from 'webapp/components/filter/no-results';
import SkeletonScreenComponent from 'webapp/components/skeleton-screen';

import type ActivityLog from 'webapp/models-custom/activity-log';
import FilterSortActivityLog, {
	ALL_ACCOUNTS,
	ALL_EVENTS,
	LogFilterId,
	TimeFrame,
} from 'webapp/models-custom/filter-sort/activity-log';

import type {
	ActivityLogAccountQueryParams,
	ActivityLogsQueryParams,
} from 'webapp/services/model-manager/activity-logs';
import type { RSjqXHR } from 'webapp/services/request';

interface Args {
	objectId?: number;
	// when set, it pins the requested object types and the Feature filter selection is ignored
	objectTypes?: ActivityLogObjectType[];
	// an item's own log endpoint, requested instead of /activity-logs; it takes only paging
	path?: string;
	onRequestFinished?: () => void;
	groupForView?: string;
}

const DEFAULT_PAGE = 1;
const DEFAULT_PAGE_SIZE = 100;

export default class AccountLogIndexComponent extends Component<Args> {
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('model-manager/activity-logs')
	declare mmActivityLogsService: Service.ModelManager.ActivityLogs;
	@service('request') declare requestService: Service.Request;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('account') declare accountService: Service.Account;
	@service('event-manager') declare eventManagerService: Service.EventManager;
	@service('react') declare reactService: Service.React;

	@tracked allActivityLogs: ActivityLog[] = [];
	@tracked currentPage = DEFAULT_PAGE;
	@tracked totalItems = 0;
	@tracked pageSize = DEFAULT_PAGE_SIZE;
	@tracked isLoading = false;

	timezone = 'UTC';
	eventIds: ActivityLogEvent[] = [];
	objectTypes: ActivityLogObjectType[] = [];

	// FILTER SELECTION
	timeFrame: TimeFrame | null = null;
	userId: number | null = null;
	searchString: string | null = null;
	accountSpaceId: number | null = null;

	// REQUEST
	previewGraphLoadingXHR: RSjqXHR | null = null;

	visibleCheckContainerNode: HTMLElement | null = null;
	filterSort: FilterSortActivityLog;

	reactElement: ReactIntegrations.Root = null;

	emailFunctions?: EmailActivityWrapperObject;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		const userTimezone = this.currentUserService.model?.timezone;
		if (userTimezone) {
			this.timezone = userTimezone;
		}

		const filterSort = new FilterSortActivityLog({
			getAllItems: () => {
				return this.allActivityLogs;
			},
			afterFilter: () => {
				// for visible-check
				const node = this.visibleCheckContainerNode;
				if (node) {
					this.triggerContentChange(node);
				}

				const searchString = this.filterSort.typedFilter;

				let timeFrame: TimeFrame | null = null,
					accountSpaceId: number | null = null,
					userId: number | null = null,
					objectTypes: ActivityLogObjectType[] = [],
					eventIds: ActivityLogEvent[] = [];

				this.filterSort.filters.forEach((filter) => {
					switch (filter.id) {
						case LogFilterId.ACCOUNT:
							if (!this.isSpecificItem) {
								accountSpaceId = filter.selected[0] as number;
							}
							break;

						case LogFilterId.TIME_FRAME:
							timeFrame = this.isSpecificItem
								? TimeFrame.ALL_TIME
								: (filter.selected[0] as TimeFrame);
							break;

						case LogFilterId.USERS:
							if (!this.isSpecificItem) {
								userId = filter.selected ? (filter.selected[0] as number) : null;
							}
							break;

						case LogFilterId.FEATURE:
							objectTypes = this.getObjectTypeArray(
								filter.selected[0] as ActivityLogFeature,
							);
							break;

						case LogFilterId.EVENT:
							if (!this.isSpecificItem) {
								eventIds = (filter.selected as number[]).filter(
									(id) => id > ALL_EVENTS,
								);
							}
							break;
					}
				});

				const isAccountChanged = this.accountSpaceId !== accountSpaceId;

				if (
					isAccountChanged ||
					this.timeFrame !== timeFrame ||
					this.userId !== userId ||
					!equal(this.objectTypes, objectTypes) ||
					!equal(this.eventIds, eventIds) ||
					this.searchString !== searchString
				) {
					this.accountSpaceId = accountSpaceId;
					this.timeFrame = timeFrame;
					this.userId = userId;
					this.objectTypes = objectTypes;
					this.eventIds = eventIds;
					this.searchString = searchString;
					this.currentPage = DEFAULT_PAGE;

					if (isAccountChanged) {
						this.updateAccountUsers();
					}
					this.requestActivityLogs();
				}
			},
		});

		this.filterSort = filterSort;
	}

	get isEmpty() {
		return !this.allActivityLogs.length;
	}

	get isSpecificItem() {
		return !!this.args.objectId || !!this.args.path;
	}

	get showTableActions() {
		return !this.isSpecificItem;
	}

	get isEmptyWithDefaultFilters() {
		return (
			this.isEmpty && !this.filterSort.isSearchActive && !this.filterSort.isAnyFilterActive
		);
	}

	get showAccountColumn() {
		return !this.isSpecificItem && this.accountService.isAgency;
	}

	private get accountQueryParameters(): ActivityLogAccountQueryParams | null {
		const accountSpaceId = this.accountSpaceId;

		if (accountSpaceId === null) {
			return null;
		}

		if (accountSpaceId === ALL_ACCOUNTS) {
			return { fetchType: ActivityLogFetchType.ALL };
		}

		return {
			fetchType:
				accountSpaceId === this.filterSort.agencySpaceId
					? ActivityLogFetchType.SPACE
					: ActivityLogFetchType.CLIENTS,
			spaceId: accountSpaceId,
		};
	}

	private updateAccountUsers() {
		const accountQueryParameters = this.accountQueryParameters;

		if (
			!accountQueryParameters ||
			accountQueryParameters.fetchType === ActivityLogFetchType.SPACE
		) {
			this.filterSort.accountUsers = null;
			return;
		}

		this.filterSort.accountUsers = [];

		void this.mmActivityLogsService
			.getActivityLogUsers(accountQueryParameters)
			.then((reply) => {
				if (equal(this.accountQueryParameters, accountQueryParameters)) {
					this.filterSort.accountUsers = reply.users;
				}
			})
			.catch(this.requestService.showAllValidationErrors.bind(this.requestService));
	}

	private resetAccountFilter() {
		const accountFilter = this.filterSort.findFilter(LogFilterId.ACCOUNT);

		if (accountFilter && !accountFilter.isDefaultSelected) {
			accountFilter.selectOption(accountFilter.defaultValue);
		}
	}

	private requestActivityLogs() {
		if (this.previewGraphLoadingXHR && this.previewGraphLoadingXHR.readyState !== 4) {
			this.previewGraphLoadingXHR.abort();
			this.isLoading = false;
		}

		this.isLoading = true;
		const promise = this.getActivityLogs(this.getQueryParameters());

		const accountQueryParameters = this.accountQueryParameters;

		this.previewGraphLoadingXHR = promise.jqPromise as RSjqXHR;
		this.previewGraphLoadingXHR.fail((jqXHR) => {
			if (
				jqXHR.status === 403 &&
				!this.isSpecificItem &&
				equal(this.accountQueryParameters, accountQueryParameters)
			) {
				this.resetAccountFilter();
			}
		});

		promise
			.then((reply) => {
				return this.mmActivityLogsService.fetchObjectPrerequisites(reply).then(() => {
					return Promise.resolve(reply);
				});
			})
			.then((reply) => {
				this.allActivityLogs = this.mmActivityLogsService.createModelsFromRecordsArray(
					reply.activityLogs,
				);
				this.totalItems = reply.pagination.totalItems || 0;
			})
			.catch(this.requestService.showAllValidationErrors.bind(this.requestService))
			.finally(() => {
				if (this.args.onRequestFinished) {
					this.args.onRequestFinished();
				}

				this.isLoading = false;
			});
	}

	private getQueryParameters(): ActivityLogsQueryParams {
		const pagingQueryParameters: ActivityLogsQueryParams = {
			pageSize: this.pageSize,
			page: this.currentPage - 1,
			includeMessages: true,
		};

		if (this.args.path) {
			return pagingQueryParameters;
		}

		const afterTimestamp = this.getEpochUnixTimestampFromTimeFrame(),
			userId = this.userId,
			searchString = this.searchString,
			objectId = this.args.objectId,
			objectTypes = this.args.objectTypes ?? this.objectTypes,
			eventIds = this.eventIds,
			requiredQueryParameters: ActivityLogsQueryParams = {
				...pagingQueryParameters,
				...this.accountQueryParameters,
			};

		if (afterTimestamp) {
			requiredQueryParameters.after = afterTimestamp;
		}

		if (userId) {
			requiredQueryParameters.userId = userId;
		}

		if (searchString) {
			requiredQueryParameters.searchString = searchString;
		}

		if (objectId) {
			requiredQueryParameters.objectId = objectId;
		}

		if (objectTypes.length) {
			requiredQueryParameters.objectTypes = objectTypes;
		}

		if (eventIds.length) {
			requiredQueryParameters.eventIds = eventIds;
		}

		const groupForView = this.args.groupForView;
		if (groupForView) {
			requiredQueryParameters.groupForView = groupForView;
		}

		return requiredQueryParameters;
	}

	private getObjectTypeArray(selectedFeature: ActivityLogFeature) {
		switch (selectedFeature) {
			case ActivityLogFeature.BOARDS:
				return [ActivityLogObjectType.BOARD, ActivityLogObjectType.BOARD_TEMPLATE];
			case ActivityLogFeature.LOOPS:
				return [ActivityLogObjectType.BOARD_LOOP];
			case ActivityLogFeature.METRICS:
				return [
					ActivityLogObjectType.CALCULATED_METRIC,
					ActivityLogObjectType.CUSTOM_QUERY,
					ActivityLogObjectType.METRIC_DIGEST,
				];
			case ActivityLogFeature.REPORTS:
				return [ActivityLogObjectType.REPORT, ActivityLogObjectType.REPORT_TEMPLATE];
			case ActivityLogFeature.DATA_SOURCES:
				return [ActivityLogObjectType.DATA_SOURCE];
			case ActivityLogFeature.GOALS:
				return [ActivityLogObjectType.GOAL];
			case ActivityLogFeature.SCORECARDS:
				return [ActivityLogObjectType.SCORECARD];
			case ActivityLogFeature.ALERTS:
				return [ActivityLogObjectType.ALERT];
			case ActivityLogFeature.SNAPSHOTS:
				return [ActivityLogObjectType.SCHEDULED_SNAPSHOT];
			case ActivityLogFeature.PLANS_AND_BILLING:
				return [ActivityLogObjectType.BILLING];
			case ActivityLogFeature.USERS:
				return [ActivityLogObjectType.USER];
			case ActivityLogFeature.ACCOUNT:
				return [ActivityLogObjectType.SPACE];
			case ActivityLogFeature.COMPANY_INFORMATION:
				return [ActivityLogObjectType.COMPANY];
			case ActivityLogFeature.SSO:
				return [ActivityLogObjectType.IDENTITY_PROVIDER];
			case ActivityLogFeature.GENIE:
				return [
					ActivityLogObjectType.GENIE_CHAT,
					ActivityLogObjectType.GENIE_ROUTINE,
					ActivityLogObjectType.GENIE_ARTIFACT,
					ActivityLogObjectType.GENIE_SKILL,
					ActivityLogObjectType.GENIE_CONNECTOR,
					ActivityLogObjectType.GENIE_MCP,
					ActivityLogObjectType.GENIE_MEMORY,
				];
			default:
				return [];
		}
	}

	private getEpochUnixTimestampFromTimeFrame(): number | null {
		const now = moment().tz(this.timezone);

		switch (this.timeFrame) {
			case TimeFrame.ALL_TIME:
				return null;
			case TimeFrame.TODAY:
				now.startOf('day');
				break;
			case TimeFrame.LAST_7_DAYS:
				now.subtract(7, 'days');
				break;
			case TimeFrame.LAST_30_DAYS:
				now.subtract(30, 'days');
				break;
			case TimeFrame.LAST_90_DAYS:
				now.subtract(90, 'days');
				break;
		}

		return now.unix();
	}

	private getActivityLogs(queryParams: ActivityLogsQueryParams) {
		return this.requestService.api<APIData.Settings.ActivityLogResponse>(
			'GET',
			this.args.path ?? '/activity-logs',
			queryParams,
		);
	}

	@action
	changePage(newPage: number) {
		this.currentPage = newPage;
		debounce(this, this.requestActivityLogs, 200);
	}

	@action
	triggerContentChange(node: HTMLElement): void {
		// for visible-check
		node.dispatchEvent(new Event(containerEventName));
	}

	@action
	onReactInit(args: EmailActivityWrapperObject) {
		this.emailFunctions = args;
	}

	@action
	openEmailDetailsDialog(uniqueReferenceId: string | null) {
		if (!uniqueReferenceId) {
			return;
		}

		this.emailFunctions?.displayEmailActivityDialog(uniqueReferenceId);
	}

	@action
	setupView(node: HTMLElement) {
		this.reactElement = this.reactService.create(node as HTMLDivElement);
		this.renderReactComponent();

		this.eventManagerService.on(
			AppEvent.ACCESS_TOKEN_REFRESHED,
			this,
			this.renderReactComponent,
		);
		this.eventManagerService.on(AppEvent.THEME_CHANGED, this, this.renderReactComponent);
	}

	@action
	teardownView() {
		this.reactService.unmount(this.reactElement);

		this.eventManagerService.off(AppEvent.ACCESS_TOKEN_REFRESHED, this);
		this.eventManagerService.off(AppEvent.THEME_CHANGED, this);
	}

	renderReactComponent() {
		const { accessToken, userDataToken: dataToken } = this.currentUserService;

		const props: EmailActivityEntryPointProps = {
			accessToken: accessToken!,
			dataToken: dataToken!,
			isDarkThemeActive: this.navigationService.theme.isDark,
			spaceId: this.accountService.id,
			onInit: this.onReactInit,
		};

		if (this.reactElement) {
			this.reactService.render(this.reactElement, EmailActivity(props));
		}
	}
	<template>
		{{#unless this.isSpecificItem}}

			<FilterLineProxyComponent @model={{this.filterSort}} />

		{{/unless}}

		{{#if this.isLoading}}

			<SkeletonScreenComponent @type="table" />

		{{else}}

			{{#if this.isEmpty}}

				{{#if this.isSpecificItem}}

					<div class="content__empty">
						There are no events in your Activity log. If you think this is an error,
						don't hesitate to get in touch with our customer support.
					</div>

				{{else if this.isEmptyWithDefaultFilters}}

					<div class="noResults">
						There was no activity in the last 30 days. Choose a longer time frame to see
						older events.
					</div>

				{{else}}

					<FilterNoResultsComponent @model={{this.filterSort}} />

				{{/if}}

			{{/if}}

			<div class="table-wrapper">
				<AccountLogsTableComponent
					@model={{this.allActivityLogs}}
					@filterSort={{this.filterSort}}
					@showActions={{this.showTableActions}}
					@showAccount={{this.showAccountColumn}}
					@openEmailDetailsDialog={{this.openEmailDetailsDialog}}
				/>
			</div>

			<AccountLogsPageNavigationComponent
				@currentPage={{this.currentPage}}
				@pageSize={{this.pageSize}}
				@totalItems={{this.totalItems}}
				@changePage={{this.changePage}}
			/>

			<div {{didInsert this.setupView}} {{willDestroy this.teardownView}}>
			</div>
		{{/if}}
	</template>
}
